"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useEffect, useCallback, type JSX } from "react";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { TabToggleWithOverflow } from "@/components/common/TabToggleWithOverflow";
import WaveHeader, { WaveHeaderPinnedSide } from "@/components/waves/header/WaveHeader";
import { WaveWinnersSmall } from "@/components/waves/winners/WaveWinnersSmall";
import BrainRightSidebarContent from "./BrainRightSidebarContent";
import BrainRightSidebarFollowers from "./BrainRightSidebarFollowers";
import { Mode, SidebarTab } from "./BrainRightSidebar";
import { WaveSmallLeaderboard } from "@/components/waves/small-leaderboard/WaveSmallLeaderboard";
import { WaveLeaderboardRightSidebarVoters } from "@/components/waves/leaderboard/sidebar/WaveLeaderboardRightSidebarVoters";
import { WaveLeaderboardRightSidebarActivityLogs } from "@/components/waves/leaderboard/sidebar/WaveLeaderboardRightSidebarActivityLogs";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
interface WaveContentProps {
  readonly wave: ApiWave;
  readonly mode: Mode;
  readonly setMode: (mode: Mode) => void;
  readonly activeTab: SidebarTab;
  readonly setActiveTab: (tab: SidebarTab) => void;
}
interface TabOption {
  key: SidebarTab;
  label: string;
}
export const WaveContent: React.FC<WaveContentProps> = t0 => {
  const $ = _c(68);
  const {
    wave,
    mode,
    setMode,
    activeTab,
    setActiveTab
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== mode || $[1] !== setMode) {
    t1 = () => setMode(mode === Mode.FOLLOWERS ? Mode.CONTENT : Mode.FOLLOWERS);
    $[0] = mode;
    $[1] = setMode;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onFollowersClick = t1;
  let t2;
  if ($[3] !== pathname || $[4] !== router || $[5] !== searchParams) {
    t2 = drop => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.set("drop", drop.id);
      router.push(`${pathname}?${params.toString()}`, {
        scroll: false
      });
    };
    $[3] = pathname;
    $[4] = router;
    $[5] = searchParams;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const onDropClick = t2;
  const isRankWave = wave.wave.type === ApiWaveType.Rank;
  const {
    voting: t3,
    decisions: t4
  } = useWaveTimers(wave);
  const {
    isCompleted
  } = t3;
  const {
    firstDecisionDone
  } = t4;
  let t5;
  let t6;
  if ($[7] !== activeTab || $[8] !== firstDecisionDone || $[9] !== isCompleted || $[10] !== setActiveTab) {
    t5 = () => {
      const isLeaderboardAndVotingEnded = activeTab === SidebarTab.LEADERBOARD && isCompleted;
      const isWinnersAndFirstDecisionNotPassed = activeTab === SidebarTab.WINNERS && !firstDecisionDone;
      if (isLeaderboardAndVotingEnded || isWinnersAndFirstDecisionNotPassed) {
        setActiveTab(SidebarTab.ABOUT);
      }
    };
    t6 = [isCompleted, firstDecisionDone, activeTab, setActiveTab];
    $[7] = activeTab;
    $[8] = firstDecisionDone;
    $[9] = isCompleted;
    $[10] = setActiveTab;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      key: SidebarTab.ABOUT,
      label: "About"
    };
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let tabs;
  if ($[14] !== firstDecisionDone || $[15] !== isCompleted) {
    tabs = [t7];
    if (!isCompleted) {
      let t8;
      if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = {
          key: SidebarTab.LEADERBOARD,
          label: "Leaderboard"
        };
        $[17] = t8;
      } else {
        t8 = $[17];
      }
      tabs.push(t8);
    }
    if (firstDecisionDone) {
      let t8;
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = {
          key: SidebarTab.WINNERS,
          label: "Winners"
        };
        $[18] = t8;
      } else {
        t8 = $[18];
      }
      tabs.push(t8);
    }
    let t8;
    let t9;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        key: SidebarTab.TOP_VOTERS,
        label: "Voters"
      };
      t9 = {
        key: SidebarTab.ACTIVITY_LOG,
        label: "Activity"
      };
      $[19] = t8;
      $[20] = t9;
    } else {
      t8 = $[19];
      t9 = $[20];
    }
    tabs.push(t8, t9);
    $[14] = firstDecisionDone;
    $[15] = isCompleted;
    $[16] = tabs;
  } else {
    tabs = $[16];
  }
  const options = tabs;
  let t8;
  if ($[21] !== onFollowersClick || $[22] !== wave) {
    t8 = <WaveHeader wave={wave} onFollowersClick={onFollowersClick} useRing={false} useRounded={false} pinnedSide={WaveHeaderPinnedSide.LEFT} />;
    $[21] = onFollowersClick;
    $[22] = wave;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== mode || $[25] !== setMode || $[26] !== wave) {
    t9 = mode === Mode.CONTENT ? <BrainRightSidebarContent wave={wave} /> : <BrainRightSidebarFollowers wave={wave} closeFollowers={() => setMode(Mode.CONTENT)} />;
    $[24] = mode;
    $[25] = setMode;
    $[26] = wave;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t8 || $[29] !== t9) {
    t10 = <div className="tw-h-full tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-700">{t8}{t9}</div>;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== onDropClick || $[32] !== wave) {
    t11 = <div><WaveSmallLeaderboard wave={wave} onDropClick={onDropClick} /></div>;
    $[31] = onDropClick;
    $[32] = wave;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== onDropClick || $[35] !== wave) {
    t12 = <div><WaveWinnersSmall wave={wave} onDropClick={onDropClick} /></div>;
    $[34] = onDropClick;
    $[35] = wave;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== wave) {
    t13 = <div className="tw-p-4"><WaveLeaderboardRightSidebarVoters wave={wave} /></div>;
    $[37] = wave;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== wave) {
    t14 = <div className="tw-p-4"><WaveLeaderboardRightSidebarActivityLogs wave={wave} /></div>;
    $[39] = wave;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t10 || $[42] !== t11 || $[43] !== t12 || $[44] !== t13 || $[45] !== t14) {
    t15 = {
      [SidebarTab.ABOUT]: t10,
      [SidebarTab.LEADERBOARD]: t11,
      [SidebarTab.WINNERS]: t12,
      [SidebarTab.TOP_VOTERS]: t13,
      [SidebarTab.ACTIVITY_LOG]: t14
    };
    $[41] = t10;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  const rankWaveComponents = t15;
  if (!isRankWave) {
    let t16;
    if ($[47] !== onFollowersClick || $[48] !== wave) {
      t16 = <WaveHeader wave={wave} onFollowersClick={onFollowersClick} useRing={false} useRounded={false} pinnedSide={WaveHeaderPinnedSide.LEFT} />;
      $[47] = onFollowersClick;
      $[48] = wave;
      $[49] = t16;
    } else {
      t16 = $[49];
    }
    let t17;
    if ($[50] !== mode || $[51] !== setMode || $[52] !== wave) {
      t17 = mode === Mode.CONTENT ? <BrainRightSidebarContent wave={wave} /> : <BrainRightSidebarFollowers wave={wave} closeFollowers={() => setMode(Mode.CONTENT)} />;
      $[50] = mode;
      $[51] = setMode;
      $[52] = wave;
      $[53] = t17;
    } else {
      t17 = $[53];
    }
    let t18;
    if ($[54] !== t16 || $[55] !== t17) {
      t18 = <div className="tw-h-full tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-700">{t16}{t17}</div>;
      $[54] = t16;
      $[55] = t17;
      $[56] = t18;
    } else {
      t18 = $[56];
    }
    return t18;
  }
  let t16;
  if ($[57] !== setActiveTab) {
    t16 = key => setActiveTab(key as SidebarTab);
    $[57] = setActiveTab;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  let t17;
  if ($[59] !== activeTab || $[60] !== options || $[61] !== t16) {
    t17 = <div className="tw-pb-px tw-pl-2.5"><TabToggleWithOverflow options={options} activeKey={activeTab} onSelect={t16} maxVisibleTabs={3} /></div>;
    $[59] = activeTab;
    $[60] = options;
    $[61] = t16;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  const t18 = rankWaveComponents[activeTab];
  let t19;
  if ($[63] !== t18) {
    t19 = <div>{t18}</div>;
    $[63] = t18;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  let t20;
  if ($[65] !== t17 || $[66] !== t19) {
    t20 = <>{t17}{t19}</>;
    $[65] = t17;
    $[66] = t19;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  return t20;
};